import React, { useEffect, useState } from "react";

/**
 * MODERN 2026 SUCCESS PAGE (UPGRADED)
 * - Stripe verification
 * - Progress bar + step tracker
 * - Responsive UI
 * - Smooth animations
 */

export default function Success() {
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);

  const sessionId =
    new URLSearchParams(window.location.search).get("session_id") || "N/A";

  useEffect(() => {
    async function verify() {
      try {
        const res = await fetch("/api/payment/verify-session", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionId }),
        });

        const data = await res.json();
        setPayment(data);
      } catch (err) {
        console.error("Payment verification failed:", err);
      } finally {
        setLoading(false);
      }
    }

    verify();
  }, [sessionId]);

  const steps = [
    "Payment Received",
    "Verification",
    "Consultation",
    "Installation",
    "Handover",
  ];

  // More realistic stage handling (safe fallback)
  const stageMap = {
    pending: 0,
    paid: 1,
    verified: 2,
    consultation: 3,
    installation: 4,
    completed: 5,
  };

  const currentStep =
    stageMap[payment?.stage] ??
    (payment?.status === "paid" ? 2 : 1);

  const progressPercent = ((currentStep + 1) / steps.length) * 100;

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        {/* HERO */}
        <div style={styles.hero}>
          <div style={styles.successIcon}>✓</div>
          <div style={styles.secureBadge}>🔒 Stripe Payment Verified</div>

          <h1 style={styles.heroTitle}>
            {loading ? "Verifying Payment..." : "Payment Successful"}
          </h1>

          <p style={styles.heroText}>
            {payment?.message ||
              "Thank you for your purchase. Our team will contact you within 24 hours."}
          </p>
        </div>

        {/* STATS */}
        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div style={{ ...styles.statValue, color: "#16a34a" }}>
              {payment?.status || "Processing"}
            </div>
            <div style={styles.statLabel}>Status</div>
          </div>

          <div style={styles.statCard}>
            <div style={styles.statValue}>
              {payment?.reference || sessionId}
            </div>
            <div style={styles.statLabel}>Reference</div>
          </div>

          <div style={styles.statCard}>
            <div style={{ ...styles.statValue, color: "#2563eb" }}>
              {"<24h"}
            </div>
            <div style={styles.statLabel}>Response Time</div>
          </div>
        </div>

        {/* PROGRESS */}
        <h3 style={{ marginTop: "30px" }}>Project Progress</h3>

        {/* Progress Bar */}
        <div style={styles.progressBarContainer}>
          <div
            style={{
              ...styles.progressBarFill,
              width: `${progressPercent}%`,
            }}
          />
        </div>

        <div style={styles.progressText}>
          {Math.round(progressPercent)}% completed
        </div>

        {/* STEP TRACKER */}
        <div style={styles.progressWrap}>
          {steps.map((step, i) => {
            const status =
              i < currentStep
                ? "done"
                : i === currentStep
                ? "current"
                : "pending";

            return (
              <React.Fragment key={i}>
                <div
                  style={{
                    ...styles.step,
                    ...(status === "done"
                      ? styles.stepDone
                      : status === "current"
                      ? styles.stepCurrent
                      : styles.stepPending),
                  }}
                >
                  {status === "done" ? "✓" : "○"}
                </div>

                {i !== steps.length - 1 && <div style={styles.line} />}
              </React.Fragment>
            );
          })}
        </div>

        {/* NEXT STEPS */}
        <div style={styles.section}>
          <h3>What Happens Next?</h3>
          <ul>
            <li>Technical review of your requirements</li>
            <li>Customer consultation call</li>
            <li>Project scheduling & planning</li>
            <li>Implementation & deployment</li>
            <li>Final handover & support</li>
          </ul>
        </div>

        {/* CTA */}
        <div style={styles.ctaRow}>
          <button style={styles.primaryBtn}>Download Invoice</button>
          <button style={styles.secondaryBtn}>Contact Support</button>
        </div>

        {/* SUPPORT */}
        <div style={styles.section}>
          <h3>Need Help?</h3>
          <p>support@myidealtd.co.uk</p>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    padding: "40px",
    background: "linear-gradient(180deg,#f8fafc,#eef2ff)",
    fontFamily: "Arial, sans-serif",
  },

  card: {
    maxWidth: "1000px",
    margin: "0 auto",
    padding: "40px",
    borderRadius: "32px",
    background: "rgba(255,255,255,.75)",
    backdropFilter: "blur(24px)",
    boxShadow: "0 30px 80px rgba(0,0,0,.08)",
  },

  hero: { textAlign: "center" },

  successIcon: {
    fontSize: "60px",
    color: "#22c55e",
  },

  secureBadge: {
    margin: "10px auto",
    color: "#64748b",
  },

  heroTitle: {
    fontSize: "2.5rem",
  },

  heroText: {
    maxWidth: "700px",
    margin: "10px auto",
    color: "#475569",
  },

  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "16px",
    marginTop: "24px",
  },

  statCard: {
    padding: "20px",
    borderRadius: "20px",
    background: "#fff",
    boxShadow: "0 10px 20px rgba(0,0,0,0.05)",
  },

  statValue: {
    fontWeight: 700,
    fontSize: "18px",
  },

  statLabel: {
    color: "#64748b",
    fontSize: "13px",
  },

  /* PROGRESS BAR */
  progressBarContainer: {
    width: "100%",
    height: "10px",
    background: "#e2e8f0",
    borderRadius: "20px",
    overflow: "hidden",
    marginTop: "12px",
  },

  progressBarFill: {
    height: "100%",
    background: "linear-gradient(90deg, #22c55e, #2563eb)",
    transition: "width 0.6s ease-in-out",
  },

  progressText: {
    marginTop: "8px",
    fontSize: "13px",
    color: "#64748b",
  },

  /* STEP TRACKER */
  progressWrap: {
    display: "flex",
    alignItems: "center",
    margin: "25px 0",
    flexWrap: "wrap",
  },

  step: {
    fontSize: "18px",
    transition: "all 0.3s ease",
  },

  stepDone: {
    color: "#16a34a",
    transform: "scale(1.1)",
  },

  stepCurrent: {
    color: "#2563eb",
    transform: "scale(1.2)",
  },

  stepPending: {
    color: "#94a3b8",
  },

  line: {
    flex: 1,
    height: "3px",
    background: "#cbd5e1",
  },

  section: {
    marginTop: "20px",
    padding: "20px",
    background: "#fff",
    borderRadius: "20px",
  },

  ctaRow: {
    display: "flex",
    gap: "12px",
    marginTop: "20px",
    flexWrap: "wrap",
  },

  primaryBtn: {
    padding: "12px 18px",
    borderRadius: "12px",
    border: "none",
    background: "#2563eb",
    color: "#fff",
    cursor: "pointer",
  },

  secondaryBtn: {
    padding: "12px 18px",
    borderRadius: "12px",
    border: "1px solid #cbd5e1",
    background: "#fff",
    cursor: "pointer",
  },
};